import {
  Injectable,
  inject,
  Injector,
  DestroyRef,
  PLATFORM_ID,
  afterNextRender,
  runInInjectionContext,
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';

/**
 * This service sets the following SEO-related tags:
 * ```html
 * <meta name="description" content="...">
 * <meta name="title" content="...">
 * <meta property="og:title" content="...">
 * <meta property="og:description" content="...">
 * <meta property="og:url" content="...">
 * <meta property="og:type" content="...">
 * <meta name="twitter:card" content="...">
 * <meta name="twitter:title" content="...">
 * <meta name="twitter:description" content="...">
 * <meta name="twitter:image" content="...">
 * ```
 *
 * It also sets the document `<title>`, the canonical link, the robots meta
 * and one JSON-LD script.
 *
 * All methods will run instantly in the server and wait for the next render
 * in the browser.
 */
/** The `<title>` of `index.html`, which every page without its own title shows. */
const DEFAULT_DOCUMENT_TITLE =
  'Padspin: Most Affordable Apartments For Rent in NYC, Los Angeles, Philadelphia & Boston.';

const JSON_LD_SCRIPT_ID = 'padspin-json-ld';

@Injectable({
  providedIn: 'root',
})
export class MetadataService {
  private readonly meta = inject(Meta);
  private readonly titleService = inject(Title);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly injector = inject(Injector);
  private readonly document = inject(DOCUMENT);

  private readonly defaultTitle: string =
    'Padspin: Rent 100% No Broker Fee Apartments';
  private readonly defaultDescription: string =
    'No scams, fake listings, or ridiculous fees';
  private readonly defaultImage: string | undefined = undefined;
  private readonly defaultType: 'website' | 'product' | 'article' = 'website';
  private readonly defaultSiteName: string =
    'Padspin: Rent 100% No Broker Fee Apartments';

  constructor() {
    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.reset();
        }
      });
  }

  setRequiredTags(config: {
    title?: string;
    description?: string;
    type?: MetadataService['defaultType'];
    url?: string;
  }): void {
    this.setTitle(config.title);
    this.setDescription(config.description);
    this.setOgType(config.type);
    this.setOgUrl(config.url);
  }

  setTitle(title?: string) {
    const metaTitle = title ?? this.defaultTitle;
    this.delayActionsInBrowser(() => {
      this.titleService.setTitle(title ?? DEFAULT_DOCUMENT_TITLE);
      this.meta.removeTag('name="title"');
      this.meta.addTag({
        name: 'title',
        content: metaTitle,
      });
      this.meta.updateTag({
        property: 'og:title',
        content: metaTitle,
      });
      this.meta.updateTag({
        name: 'twitter:title',
        content: metaTitle,
      });
    });
  }

  setDescription(description = this.defaultDescription) {
    this.delayActionsInBrowser(() => {
      this.meta.removeTag('name="description"');
      this.meta.addTag({
        name: 'description',
        content: description,
      });
      this.meta.updateTag({
        property: 'og:description',
        content: description,
      });
      this.meta.updateTag({
        name: 'twitter:description',
        content: description,
      });
    });
  }

  setOgUrl(url = `https://padspin.com${this.router.url}`) {
    this.delayActionsInBrowser(() => {
      this.meta.updateTag({
        property: 'og:url',
        content: url,
      });
    });
  }

  setOgType(type = this.defaultType) {
    this.delayActionsInBrowser(() => {
      this.meta.updateTag({
        property: 'og:type',
        content: type,
      });
    });
  }

  setOgSiteName(siteName = this.defaultSiteName) {
    this.delayActionsInBrowser(() => {
      this.meta.updateTag({
        property: 'og:site_name',
        content: siteName,
      });
    });
  }

  setOgImage(imageUrl: string | undefined = this.defaultImage) {
    this.delayActionsInBrowser(() => {
      if (!imageUrl) {
        this.meta.removeTag('property="og:image"');
        this.meta.removeTag('name="twitter:image"');
        this.meta.updateTag({ name: 'twitter:card', content: 'summary' });
        return;
      }
      this.meta.updateTag({
        property: 'og:image',
        content: imageUrl,
      });
      this.meta.updateTag({
        name: 'twitter:image',
        content: imageUrl,
      });
      this.meta.updateTag({
        name: 'twitter:card',
        content: 'summary_large_image',
      });
    });
  }

  /**
   * Sets one `<script type="application/ld+json">` to `data`, or removes it
   * when `data` is undefined. Cleared on every navigation by {@link reset}.
   * `<` is escaped so text inside `data` (listing descriptions are scraped
   * or typed by third parties) cannot close the script element.
   */
  setJsonLd(data?: Record<string, unknown>) {
    this.delayActionsInBrowser(() => {
      const existing = this.document.head.querySelector(
        `script#${JSON_LD_SCRIPT_ID}`,
      );
      if (!data) {
        existing?.remove();
        return;
      }
      const script = existing ?? this.document.createElement('script');
      script.setAttribute('id', JSON_LD_SCRIPT_ID);
      script.setAttribute('type', 'application/ld+json');
      script.textContent = JSON.stringify(data).replace(/</g, '\\u003c');
      if (!existing) {
        this.document.head.appendChild(script);
      }
    });
  }

  /**
   * Sets `<link rel="canonical">` to `url`, or removes it when `url` is
   * undefined. Cleared on every navigation by {@link reset}.
   */
  setCanonical(url?: string) {
    this.delayActionsInBrowser(() => {
      const existing = this.document.head.querySelector<HTMLLinkElement>(
        'link[rel="canonical"]',
      );
      if (!url) {
        existing?.remove();
        return;
      }
      const link = existing ?? this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', url);
      if (!existing) {
        this.document.head.appendChild(link);
      }
    });
  }

  /**
   * Sets `<meta name="robots">` to `content` (e.g. `noindex`), or removes it
   * when `content` is undefined. Cleared on every navigation by {@link reset}.
   */
  setRobots(content?: string) {
    this.delayActionsInBrowser(() => {
      if (!content) {
        this.meta.removeTag('name="robots"');
        return;
      }
      this.meta.updateTag({ name: 'robots', content });
    });
  }

  reset(): void {
    this.setRobots();
    this.setCanonical();
    this.setJsonLd();
    this.setTitle();
    this.setDescription();
    this.setOgImage();
    this.setOgType();
    this.setOgUrl();
    this.setOgSiteName();
  }

  /** Instantly run in the server, wait for the next render in the browser. */
  private delayActionsInBrowser(action: () => void): void {
    runInInjectionContext(this.injector, () => {
      if (isPlatformBrowser(this.platformId)) {
        afterNextRender(() => action());
      } else {
        action();
      }
    });
  }
}
